import React, { Component, Fragment, ReactElement } from "react";
import Table, { TableColumn, TableItem } from "../Table";
import { TableRowProps } from "../TableRow";
import TableCell from "../TableCell";
import SchoolCell from "../SchoolCell";
import AutomationDurableFunctionInstanceStatus from "./AutomationDurableFunctionInstanceStatus";
import { FRIENDLY_DATETIME_FORMAT } from "../../utils/Utils";
import moment from "moment";
import { Spinner } from "office-ui-fabric-react";
import { DurableFunctionInstance } from "../../models/DurableFunctionInstance";
import School from "../../models/School";
import { MisDataServiceTypes, getMisDataServiceTypeDisplayInfo } from "../../models/SchoolMailroomConfigDto";

interface MailroomSchoolsTableProps {
    title?: string;
    
    items?: MailroomSchoolsTableRow[];
    
    virtualize: boolean;
    enableLinks?: boolean;

    //Allow overriding
    columns?: TableColumn[] | undefined | null;
    cellForColumn?(item: TableItem, column: TableColumn, columnIndex: number): ReactElement<TableCell> | undefined;
}

interface MailroomSchoolsTableState {
    items?: MailroomSchoolsTableRow[] | undefined | null;
}

export interface MailroomSchoolsTableRow {
    school?: School;
    lastSdsUpload?: string | "Loading";
    lastDataUpload?: string | "Loading"; 
    
    misDataServiceType?: MisDataServiceTypes;
    academicYear?: string;

    automationLoopInstance?: DurableFunctionInstance | null;
}

const columns: TableColumn[] = [
    {
        title: "Name",
        propertyPath: "school.schoolName",
        flex: 4,
        enableSort: true
    },
    {
        title: "Tenant",
        propertyPath: "school.tenantName",
        flex: 4,
        enableSort: true
    },
    {
        title: "Data last received",
        propertyPath: "lastDataUpload",
        type: "date",
        flex: 3,
        enableSort: true
    },
    {
        title: "Last Upload To SDS",
        propertyPath: "lastSdsUpload",
        type: "date",
        flex: 3,
        enableSort: true
    },
    {
        title: "Status",
        propertyPath: "automationLoopInstance.runtimeStatus",
        flex: 3,
        enableSort: true
    },
    {
        title: "Academic Year",
        propertyPath: "academicYear",
        flex: 2,
        enableSort: true
    },
    {
        title: "MIS Data Source",
        propertyPath: "misDataServiceType",
        flex: 4,
        enableSort: true
    }
];

export default class MailroomSchoolsTable extends Component<MailroomSchoolsTableProps, MailroomSchoolsTableState>
{
    constructor(props: MailroomSchoolsTableProps) {
        super(props);

        this.state = {
        }
    }

    public async componentDidMount() {
        if (this.props.items) {
            this.setState({
                items: this.props.items
            });
        }
    }


    componentDidUpdate(prevProps: Readonly<MailroomSchoolsTableProps>, prevState: Readonly<MailroomSchoolsTableState>, snapshot?: any): void {
        
        if(this.props.items && this.props.items !== prevProps.items)
        {
            this.setState({
                items: this.props.items
            })
        }
        
    }
    
    private columnRowProps = (row: MailroomSchoolsTableRow): TableRowProps => {
        return {
            enableRowLink: this.props.enableLinks === undefined ? true : this.props.enableLinks,
            to: `/cdb-admin/mailroom/${row.school!.tenantId}/${row.school!.schoolCode}`
        };
    };

    private cellForColumn = (item: MailroomSchoolsTableRow, column: TableColumn, columnIndex: number): JSX.Element | undefined => {

        if (this.props.cellForColumn) {
            var customCell = this.props.cellForColumn(item, column, columnIndex);
            if (customCell !== undefined) {
                return customCell;
            }
        }

        switch (column.propertyPath) {
            case "school.schoolName":
                return <SchoolCell key={`${columnIndex}-${column.propertyPath}`} schoolKey={item.school!} school={item.school} enableLink={false} flex={column.flex} />

            case "school.tenantName":
                return <TableCell key={`${columnIndex}-${column.propertyPath}`} flex={column.flex}>
                    <span>{item.school!.tenantName}</span>
                    <span className={"cdb-table__extra-detail"}>{item.school!.tenantId}</span>
                </TableCell>;
            
            case "automationLoopInstance.runtimeStatus":
                return <TableCell key={`${columnIndex}-${column.propertyPath}`} flex={column.flex}>
                    {MailroomSchoolsTable.AutomationLoopStatusCell(item.automationLoopInstance)}
                </TableCell>;

            case "lastDataUpload":
                return <TableCell key={`${columnIndex}-${column.propertyPath}`} flex={column.flex}>
                    {MailroomSchoolsTable.timeSinceOrLoadingCell(item.lastDataUpload)}
                </TableCell>;

            case "lastSdsUpload":
                return <TableCell key={`${columnIndex}-${column.propertyPath}`} flex={column.flex}>
                    {MailroomSchoolsTable.timeSinceOrLoadingCell(item.lastSdsUpload)}
                </TableCell>;

            case "misDataServiceType":
                return <TableCell key={`${columnIndex}-${column.propertyPath}`} flex={column.flex}>
                    {MailroomSchoolsTable.misDataServiceTypeToString(item.misDataServiceType)}
                </TableCell>;
        }
    }

    private static misDataServiceTypeToString(misDataServiceType?: MisDataServiceTypes) {
        const displayInfo = getMisDataServiceTypeDisplayInfo(misDataServiceType);
        return <Fragment>
            <span>{displayInfo.name}</span>
            <span className={"cdb-table__extra-detail"}>{displayInfo.detail}</span>
        </Fragment>;
    }

    private static timeSinceOrLoadingCell(dateTime: string | "Loading" | undefined) {
        if (dateTime == "Loading") {
            return <Spinner className={"flex--justify-content-flex-start"} label={"Loading..."} labelPosition="right" />
        }
        else {
            return MailroomSchoolsTable.TimeSinceCell(dateTime);
        }
    }

    static TimeSinceCell(date: string | Date | undefined): JSX.Element {
        if (date != undefined) {

            var momentDate = moment(date);
            var daysDiff = moment().diff(momentDate, 'days')

            var className = "";

            //Go amber after 2 days.
            if (daysDiff >= 2) {
                className += `pill pill--amber`;
            }

            //Go red after a full school week to make stand out.
            if (daysDiff >= 5) {
                className += `pill pill--red`;
            }

            return <Fragment>
                <span className={className}>{momentDate.fromNow()}</span>
                <span className="cdb-table__extra-detail">{momentDate.format(FRIENDLY_DATETIME_FORMAT)}</span>
            </Fragment>;
        }

        return <span></span>
    }
    
    static AutomationLoopStatusCell(instance?: DurableFunctionInstance | null) {
        if (instance === undefined) {
            return <Spinner className={"flex--justify-content-flex-start"} label={"Loading..."} labelPosition="right" />
        }
        else {
            return <AutomationDurableFunctionInstanceStatus durableFunctionInstance={instance!} />
        }
    }

    public render() {
        return (
            <Table
                title={this.props.title}
                items={this.state.items == undefined ? [] : this.state.items}
                columns={this.props.columns ?? columns}
                rowPropsForItem={this.columnRowProps}
                cellForColumn={this.cellForColumn}
                enableSort={true}
                defaultSortColumnPath="lastSdsUpload"
                loading={this.state.items === undefined}
                virtualize={this.props.virtualize}>
                {this.props.children}
            </Table>
        );
    }
}